/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *==================================================
 */
//Standard libraries
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import Divider from "@material-ui/core/Divider";
import {
  Grid,
  Typography,
  Box,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import { FormLabel } from "@material-ui/core";
//context
import { useCSRValue } from "../context";
//custom libraries
import { useLoginForm } from "../hooks/use-login-form";
import { Button, Paper } from "../components";
import { EMPTY_STRING } from "../constants/common";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import TextField from "@material-ui/core/TextField";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";

export const loginSchema = yup.object().shape({
  username: yup
    .string()
    .required("Email es requerido")
    .email("Email debe tener un formato válido"),
  password: yup.string().required("Contraseña es requerida"),
});

const LoginForm = () => {
  const { t } = useTranslation();
  const { csr, ses } = useCSRValue();
  const [showPassword, setShowPassword] = useState(false);

  const {
    submitLoginForm,
    changePasswordLogin,
    canChangePassword,
    onCurrentPassword,
    onPasswordNew,
    onPasswordVerify,
    cancelChangePassword,
    currentPassword,
    passwordNew,
    passwordVerify,
  } = useLoginForm();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loginSchema),
  });

  const onSubmit = (data: any) => {
    submitLoginForm(data);
  };

  return (
    <Grid
      item
      container
      alignContent="center"
      alignItems="center"
      justify="center"
      className="min-content-height login-form app-gradient">
      <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
        <Paper>
          {csr && csr.sessionError && csr.sessionError.passwordExpired ? (
            <form
              onSubmit={(e) => {
                changePasswordLogin(e);
              }}>
              <Grid
                container
                direction="column"
                justify="center"
                alignItems="stretch"
                spacing={2}>
                <Grid item>
                  <Typography variant="h3">
                    {t("SignIn.ChangePassword")}
                  </Typography>
                </Grid>

                <Grid item>
                  <Divider />
                </Grid>

                <Grid item>
                  <FormLabel error={true}>
                    {csr.sessionError.message || t(csr.sessionError.msgKey)}
                  </FormLabel>
                </Grid>

                <Grid item>
                  <TextField
                    label={t("SignIn.CurrentPassword")}
                    type="password"
                    onChange={(
                      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => {
                      onCurrentPassword(e.target?.value);
                    }}
                    value={currentPassword}
                    id="currentPassword"
                    name="currentPassword"
                    required
                    placeholder={t("SignIn.CurrentPassword")}
                    fullWidth
                  />
                </Grid>

                <Grid item xs>
                  <TextField
                    label={t("SignIn.Password")}
                    type="password"
                    onChange={(
                      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => {
                      onPasswordNew(e.target.value);
                    }}
                    id="passwordNew"
                    name="passwordNew"
                    value={passwordNew}
                    required
                    placeholder={t("SignIn.Password")}
                    fullWidth
                  />
                </Grid>
                <Grid item xs>
                  <TextField
                    label={t("SignIn.PasswordVerify")}
                    type="password"
                    onChange={(
                      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => {
                      onPasswordVerify(e.target.value);
                    }}
                    id="passwordVerify"
                    name="passwordVerify"
                    value={passwordVerify}
                    required
                    placeholder={t("SignIn.PasswordVerify")}
                    fullWidth
                    error={
                      passwordVerify.trim().length > 0 &&
                      passwordNew.trim() !== passwordVerify.trim()
                    }
                    helperText={
                      passwordVerify.trim().length > 0 &&
                      passwordNew.trim() !== passwordVerify.trim()
                        ? t("SignIn.PasswordNotMatch")
                        : EMPTY_STRING
                    }
                  />
                </Grid>
                <Grid
                  container
                  direction="row"
                  justify="space-between"
                  alignItems="center"
                  spacing={2}>
                  <Grid item>
                    <Button
                      type="submit"
                      disabled={!canChangePassword()}
                      value={t("SignIn.Submit")}
                      name={t("SignIn.Submit")}>
                      {t("SignIn.Submit")}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      color="secondary"
                      onClick={cancelChangePassword}
                      value={t("Table.Actions.Cancel")}
                      name={t("Table.Actions.Cancel")}>
                      {t("Table.Actions.Cancel")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid
                container
                direction="column"
                justify="center"
                alignItems="stretch"
                spacing={2}>
                <Grid item>
                  <Typography variant="h3">{t("SignIn.LogIn")}</Typography>
                </Grid>
                <Grid item>
                  <Divider />
                </Grid>
                {csr.sessionError && (
                  <Grid item>
                    <FormLabel error={true}>
                      {csr.sessionError.message || t(csr.sessionError.msgKey)}
                    </FormLabel>
                  </Grid>
                )}

                <Grid item>
                  <Controller
                    control={control}
                    name="username"
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        label={t("SignIn.Username")}
                        type="text"
                        onChange={onChange}
                        id="username"
                        name="username"
                        autoComplete="username"
                        value={value}
                        placeholder={t("SignIn.Username")}
                        fullWidth
                        error={errors.username ? true : false}
                        helperText={errors.username?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs>
                  <Controller
                    control={control}
                    name="password"
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        label={t("SignIn.Password")}
                        type={showPassword ? "text" : "password"}
                        onChange={onChange}
                        id="password"
                        name="password"
                        value={value}
                        placeholder={t("SignIn.Password")}
                        fullWidth
                        error={errors.password ? true : false}
                        helperText={errors.password?.message}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowPassword(!showPassword)}>
                                {showPassword && <Visibility />}
                                {!showPassword && <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item>
                  <Box display="flex" justifyContent="center">
                    <Box>
                      <Button
                        type="submit"
                        value={t("SignIn.Log_In")}
                        name={t("SignIn.Log_In")}>
                        {t("SignIn.Log_In")}
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </form>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default LoginForm;
